import { Accordion, Button } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import { PaymentMethodListRedesigned } from '../PaymentMethodList/PaymentMethodListRedesigned'
import React, { useContext, useState } from 'react'
import { usePurchaseState, useSelectedPaymentMethod } from '../../state/topaz/hooks'
import { SelectedPaymentMethod, TrackingEvent, PaymentMethodTypes, ExpandableComponent } from '../../model'
import { PaymentMethodType, ResultCode, TopazStatus } from '@afterpay/types'
import {
  resetApplePayError,
  resetCheckoutError,
  selectPurchaseMethod,
  setHasUserInitiatedChangedPaymentMethod,
  useAppDispatch,
  useAppRouter,
  useCardAddForCashCohort,
  useExpandedComponentControl,
  useCohort,
  useApplePayForCashCohort,
} from '../../state'
import { compareObjects } from '@afterpay/utils'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { parseTopazLogEventProperties } from '../../utils/topaz'
import { Route } from '../../config/router'
import { Cohort } from '@afterpaytouch/portal-api'
import { PayWithSection } from './PayWithSection'
import { useControlledState } from '../../utils/hooks'
import { SummaryNotification, SummaryNotificationContext } from '../SummaryNotification'
import { DisabledModal } from '../DisabledModal'
import { AddPaymentMethod } from './AddPaymentMethod'

const SECTION_NAME = 'Inline Payment Method'

export const InlinePaymentMethod = (): JSX.Element => {
  const { setNotification } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const { setExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.PAYMENT_METHOD_INLINE)
  const isExpanded = expandableComponentState === 'Expanded'
  const initialSelectedPaymentMethod = useSelectedPaymentMethod()
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useControlledState<SelectedPaymentMethod | undefined>(initialSelectedPaymentMethod)
  const [submitted, setSubmitted] = useState(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { status, topazApiErrorMessage } = usePurchaseState()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const cohort = useCohort()
  const isCashOnly = cohort === Cohort.CashOnly
  const isCardAddEnabled = useCardAddForCashCohort()
  const isApplePayEnabledForCashCohort = useApplePayForCashCohort()
  const hasAdditionalPaymentMethods = isCashOnly && (isCardAddEnabled || isApplePayEnabledForCashCohort)
  const addPaymentMethodHandler = (): void => {
    logEvent(TrackingEvent.PRESSED_PAYMENT_METHOD_NEW_CARD)
    router.push(Route.NEW_PAYMENT_METHOD)
  }

  const handleChange = async (payload: SelectedPaymentMethod, isSamePaymentMethod: boolean): Promise<void> => {
    try {
      if (!isSamePaymentMethod) {
        const result = await dispatch(selectPurchaseMethod(payload))
        if (payload.type === PaymentMethodType.CREDIT_CARD && result.resultCode !== ResultCode.OK) {
          logEvent(TrackingEvent.CHECKOUT_TOPAZ_ERROR, parseTopazLogEventProperties({ resultCode: result.resultCode, errorMessage: topazApiErrorMessage }))
        }
      }
      dispatch(resetCheckoutError())

      dispatch(resetApplePayError())
      dispatch(setHasUserInitiatedChangedPaymentMethod())
    } catch (e) {
      if (status > TopazStatus.Saved) {
        setNotification({ kind: 'Error', message: t('common:error:unknown'), severe: false })
        logEvent(
          TrackingEvent.CHECKOUT_TOPAZ_ERROR,
          parseTopazLogEventProperties({
            type: 'exception',
            errorCode: e?.errorCode ?? e.code,
            errorMessage: e?.message ?? e.name,
            topazStatus: TopazStatus[status],
          })
        )
      }
    }
    setSubmitted(false)
  }

  const handlePaymentMethodChange = (payload: SelectedPaymentMethod): void => {
    const isSamePaymentMethod = compareObjects(selectedPaymentMethod, payload)
    logEvent(TrackingEvent.PRESSED_PAYMENT_METHOD_SAVE_BUTTON, {
      paymentMethodType: PaymentMethodTypes[payload.type as PaymentMethodType],
      paymentMethodSelection: isSamePaymentMethod ? 'SameOption' : 'DifferentOption',
    })
    setSubmitted(true)
    setNotification(undefined)
    setSelectedPaymentMethod(payload)
    handleChange(payload, isSamePaymentMethod)
    setExpandedState('Collapsed')
  }

  const handleAccordionChange = (): void => {
    const expandableState = isExpanded ? 'Collapsed' : 'Expanded'
    logEvent(TrackingEvent.PRESSED_SUMMARY_SECTION, {
      section: SECTION_NAME,
      state: expandableState,
    })
    setExpandedState(expandableState)
  }

  // Users without a payment method see the add payment method prompt. Checked BEFORE the cash-only
  // branch: a Cash-only user with no selectable method would otherwise hit PayWithSection, which
  // renders nothing for an undefined method — leaving the section blank instead of prompting to add.
  if (!initialSelectedPaymentMethod) {
    return <AddPaymentMethod />
  }

  // Cash only users without additional payment methods see simplified payment section
  if (isCashOnly && !hasAdditionalPaymentMethods) {
    return <PayWithSection selectedPaymentMethod={selectedPaymentMethod} />
  }

  // Users with a payment method see the full accordion with payment options
  return (
    <>
      <Accordion id={ExpandableComponent.PAYMENT_METHOD_INLINE} expanded={isExpanded} onChange={handleAccordionChange} testNameSpace='payment-method-option'>
        <Accordion.Button>
          <PayWithSection selectedPaymentMethod={selectedPaymentMethod} />
        </Accordion.Button>
        <SummaryNotification />
        <Accordion.Details>
          <PaymentMethodListRedesigned onChange={handlePaymentMethodChange} current={selectedPaymentMethod} labelledBy='select-payment-method' />
          <Button.Secondary testNameSpace='add-payment-method' size='Regular' onClick={addPaymentMethodHandler} padding='Fluid'>
            {t('summary:addPaymentMethod')}
          </Button.Secondary>
        </Accordion.Details>
      </Accordion>
      {submitted ? <DisabledModal /> : null}
    </>
  )
}
